import { m } from '@pricedrop/i18n';
import { Link } from '@tanstack/react-router';
import { ItemCard } from '@/routes/items/-components/item-card';
import { ItemCardSkeleton } from '@/routes/items/-components/item-card-skeleton';

interface ItemData {
  uin: number;
  name: string;
  uinsql: string;
  brandName: string;
  price: number | null;
  highestPrice: number;
  lowestPrice: number;
  meanPrice: number;
  previousPrice: number;
}

interface FeaturedCategorySectionProps {
  title: string;
  icon: React.ReactNode;
  items?: ItemData[] | null;
  viewAllHref: string;
  loading?: boolean;
  accentClassName?: string;
}

const SKELETON_KEYS = ['s0', 's1', 's2', 's3'];

export function FeaturedCategorySection({
  title,
  icon,
  items,
  viewAllHref,
  loading = false,
  accentClassName = 'bg-gradient-to-b from-blue-500 to-blue-600',
}: FeaturedCategorySectionProps) {
  const showSkeletons = loading || items === undefined || items === null;
  const hasItems = !showSkeletons && items && items.length > 0;

  if (!showSkeletons && (!items || items.length === 0)) {
    return null;
  }

  return (
    <section className="container mx-auto px-4">
      <div className="mb-6 flex items-center gap-3">
        <div className={`h-8 w-1 rounded-full ${accentClassName}`} />
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
        </div>
        <Link
          to={viewAllHref}
          className="ms-auto text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {m.home_seeAll()}
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {showSkeletons
          ? SKELETON_KEYS.map((key) => (
              <div key={key}>
                <ItemCardSkeleton />
              </div>
            ))
          : hasItems &&
            items?.map((item, index) => (
              <div key={item.uin}>
                <ItemCard
                  name={item.name}
                  uin={item.uin}
                  uinsql={item.uinsql}
                  brandName={item.brandName}
                  price={item.price}
                  highestPrice={item.highestPrice}
                  lowestPrice={item.lowestPrice}
                  meanPrice={item.meanPrice}
                  previousPrice={item.previousPrice}
                  lazyLoad={index >= 2}
                />
              </div>
            ))}
      </div>
    </section>
  );
}
